/*
  The arithmetic every chart shares: a top for the value axis that reads as a round number, and
  the ticks under it. Kept out of the components so it is tested on its own.
*/

/**
 * The smallest round number at or above `max`: one significant step of its own magnitude, so
 * 612 tops out at 700 and 4,210 at 5,000. A chart of nothing still gets an axis to draw.
 */
export function niceMax(max: number): number {
    if (!Number.isFinite(max) || max <= 0) return 1;
    const step = 10 ** Math.floor(Math.log10(max));
    return Math.ceil(max / step) * step;
}

/**
 * `count` evenly spaced ticks from zero to `top`, zero included. A chart of nothing has the zero
 * line alone: four more lines all reading "£0" say nothing and look like a fault.
 */
export function ticks(top: number, count = 4, hasValues = true): number[] {
    if (!hasValues) return [0];
    return Array.from({ length: count + 1 }, (_, i) => (top / count) * i);
}

/**
 * A round step for an axis: 1, 2, 2.5, 5 or 10 times a power of ten, so its labels compare at a
 * glance rather than read "703.8" and "173".
 */
export function niceStep(raw: number): number {
    if (!(raw > 0)) return 1;
    const power = 10 ** Math.floor(Math.log10(raw));
    const n = raw / power;
    const multiple = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
    return multiple * power;
}

/**
 * An axis that starts off zero, for an index (the prototype's charts.js `niceAxis`): its floor is
 * a multiple of a round step at or below `base`, so 100 lands on a gridline, and the step widens
 * until `steps` of them reach `max`. Bounded, so a bad input cannot spin.
 */
export function niceAxis(base: number, max: number, steps: number): { lo: number; step: number } {
    let step = niceStep((max - base) / steps);
    let lo = Math.floor(base / step) * step;
    for (let guard = 0; lo + step * steps < max && guard < 12; guard++) {
        step = niceStep(step * 1.5);
        lo = Math.floor(base / step) * step;
    }
    return { lo, step };
}

/** The width, in px, an axis label at 11px needs: about 6.5 a character and room between. */
export function labelWidth(label: string): number {
    return label.length * 6.5 + 12;
}

/**
 * Which axis labels to draw when they do not all fit: every `step`-th, always the first and the
 * last, and none that would sit on top of the last. Points and bars are all still drawn.
 */
export function visibleLabels(labels: string[], available: number): boolean[] {
    const widest = Math.max(0, ...labels.map(labelWidth));
    const step = Math.max(1, Math.ceil((labels.length * widest) / Math.max(1, available)));
    const last = labels.length - 1;
    return labels.map((_, i) => i === last || (i % step === 0 && last - i >= step));
}

/**
 * The fill for each series, in the order the Reports palette hands them out. Spelled out in full
 * so the stylesheet's build can see every class.
 */
export const SERIES_FILL = [
    'fill-report-1',
    'fill-report-2',
    'fill-report-3',
    'fill-report-4',
    'fill-report-5',
    'fill-report-6',
] as const;

export const SERIES_STROKE = [
    'stroke-report-1',
    'stroke-report-2',
    'stroke-report-3',
    'stroke-report-4',
    'stroke-report-5',
    'stroke-report-6',
] as const;

export const SERIES_BG = [
    'bg-report-1',
    'bg-report-2',
    'bg-report-3',
    'bg-report-4',
    'bg-report-5',
    'bg-report-6',
] as const;

export type SeriesColour = 0 | 1 | 2 | 3 | 4 | 5;
